import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/shared/layouts/PageLayout';
import RulePages from '@features/rule/pages/RulePage';
import { getMascotList, getMyProfile } from '@/features/profile/api/profileService';
import { getVoucherList } from '@/features/redeem/api/redeemService';
import { ROUTES } from '@/app/router/routes';
import VoucherRedeemPopup from '../components/VoucherRedeemPopup';
import MascotActionPopup from '../components/MascotActionPopup';
import { usePageLayout } from '@/app/router/index';
import { useLanguage } from '@shared/contexts/LanguageContext';


function StaticSection({ title, subtitle, totalPoints, children, onRuleClick, isFirstCollection = false }) {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col w-full">
      <div
        className="flex items-center gap-4"
        style={{ alignSelf: 'stretch', paddingLeft: 26, borderLeft: '6px solid #BC313D' }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <div style={{ minWidth: 78 }}>
            <span
              style={(title?.length > 3) ? { color: '#171717', fontSize: 20, fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '28px' } : { color: '#171717', fontSize: 24, fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '28px' }}
            >{title}</span>
            <br />
            {subtitle && <span style={(title?.length > 3) ? { color: '#BC313D', fontSize: 20, fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '28px' } : { color: '#BC313D', fontSize: 24, fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '28px' }}>{subtitle}</span>}
          </div>
          {totalPoints != null && <span style={{ color: '#BC313D', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '28px', letterSpacing: 0.38, verticalAlign: 'bottom', marginLeft: 16 }}>
            {totalPoints > 0 ? '+' + totalPoints : totalPoints}
          </span>}
        </div>
        {isFirstCollection === true &&
          <button onClick={onRuleClick} style={{ border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }} aria-label={t('redeem.viewRulesAria')}>
            <span className="text-[#6E625B]/40 underline " style={{ textDecorationColor: '#6E625B/40', verticalAlign: 'top', fontSize: 14 }}>{t('redeem.viewRulesAria')}</span>
          </button>
        }
      </div>
      <div style={{ paddingLeft: 32 }}>
        {children}
      </div>
    </div>
  );
}

function MascotGrid({ items, loading, onMascotClick }) {
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage?.code === 'en';
  const MASCOT_Y_AXIS = [t('redeem.mascotYAxisWalk'), t('redeem.mascotYAxisStay'), t('redeem.mascotYAxisSit'), t('redeem.mascotYAxisRest')];
  const MASCOT_X_AXIS = [t('redeem.mascotXAxisGoldChild'), t('redeem.mascotXAxisElephant'), t('redeem.mascotXAxisLion'), t('redeem.mascotXAxisDeer')];

  if (loading) {
    return <div className="pl-8 py-6 text-[rgba(110,98,91,0.40)] text-sm">{t('redeem.loading')}</div>;
  }
  if (!items || items.length === 0) {
    return <div className="pl-8 py-6 text-[rgba(110,98,91,0.40)] text-sm">{t('redeem.noMascotRecords')}</div>;
  }

  const map = {};
  items.forEach((item) => {
    map[`${item.category_y}-${item.category_x}`] = item;
  });

  return (
    <div className="w-full" style={isEn ? { marginRight: -12, width: 'calc(100% + 12px)' } : {}}>
      <div className="w-full py-6 flex flex-col justify-start items-start gap-2 md:gap-5">
        <div className="flex w-full justify-between items-end gap-0 md:gap-2 lg:gap-4">
          <div className="w-10 md:w-12 lg:w-16 flex justify-center items-center shrink-0 mr-2">
            <div className="w-10 text-center text-stone-500 text-xs md:text-sm lg:text-base font-normal font-['Noto_Sans_TC'] leading-5 break-keep">{t('redeem.mascotYAxisPosture')}</div>
          </div>
          {MASCOT_X_AXIS.map((x) => (
            <div key={x} className={`flex-1 flex justify-center text-center text-stone-500 font-normal font-['Noto_Sans_TC'] leading-4 px-0.5 md:max-w-none ${isEn ? 'max-w-[56px]' : ''}`} style={{ fontSize: 10, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
              {x}
            </div>
          ))}
        </div>
        {MASCOT_Y_AXIS.map((y) => (
          <div key={y} className="flex w-full justify-between items-start gap-0 md:gap-2 lg:gap-4">
            <div className="w-10 md:w-12 lg:w-16 self-stretch flex justify-center items-start pt-4 flex-shrink-0 mr-2">
              <div className="w-10 text-center text-stone-500 text-xs md:text-sm lg:text-base font-normal font-['Noto_Sans_TC'] leading-5 break-keep">{y}</div>
            </div>
            {MASCOT_X_AXIS.map((x) => {
              const item = map[`${y}-${x}`];
              const isCollected = item?.is_collected;
              return (
                <div
                  key={x}
                  className={`flex-1 flex flex-col justify-center items-center gap-0 md:gap-1 cursor-pointer active:scale-95 transition-transform md:max-w-none ${isEn ? 'max-w-[56px]' : ''}`}
                  onClick={() => item && onMascotClick && onMascotClick(item)}
                >
                  <div className="w-full aspect-square max-w-[44px] md:max-w-[64px] lg:max-w-[80px] relative rounded-sm outline outline-1 outline-offset-[-1px] outline-black/10 flex justify-center items-center overflow-hidden"
                    style={!isCollected ? { backgroundColor: 'var(--color-neutral-100)' } : { backgroundColor: 'white' }}
                  >
                    {item?.reward_media ? (
                      <img
                        className="object-contain w-full h-full p-1"
                        src={item.reward_media}
                        alt={`${x}_${y}`}
                      />
                    ) : (
                      <div className="w-full h-full" />
                    )}
                  </div>
                  <div className="flex flex-col justify-start items-center">
                    <div className="text-center text-[#bc313d] text-sm md:text-base lg:text-lg font-medium font-['Noto_Sans_TC'] leading-6 min-h-[24px]">
                      {isCollected ? '✔' : (item?.exchange_point === 0 ? "-0" : item?.exchange_point ?? "\u00A0")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function VoucherSection({ items, loading, onVoucherClick }) {
  const { t } = useLanguage();
  if (loading) {
    return <div className="py-6 text-[rgba(110,98,91,0.40)] text-sm">{t('redeem.loading')}</div>;
  }
  if (!items || items.length === 0) {
    return <div className="pl-8 py-6 text-[rgba(110,98,91,0.40)] text-sm">{t('redeem.noVoucherRecords')}</div>;
  }

  const unusedItems = items?.filter(item => item.status === 0) || [];

  if (!unusedItems || unusedItems.length === 0) {
    return null; // Don't show if no vouchers
  }

  const groupedVouchers = unusedItems.reduce((acc, curr) => {
    if (!acc[curr.item_id]) {
      acc[curr.item_id] = { ...curr, count: 1 };
    } else {
      acc[curr.item_id].count += 1;
    }
    return acc;
  }, {});

  const voucherList = Object.values(groupedVouchers);

  return (
    <div className="w-full flex flex-col gap-4 mt-6 md:mt-8 px-4 md:px-[32px]">
      <div className="flex flex-col gap-3 w-full">
        {voucherList.map(item => (
          <div
            key={item.item_id}
            className="relative w-full h-16 flex rounded-sm overflow-hidden bg-white cursor-pointer active:scale-[0.98] transition-transform"
            onClick={() => onVoucherClick && onVoucherClick(item)}
          >
            {/* Left section */}
            <div className="flex-1 flex items-center pl-8 md:pl-[41px]">
              <span className="text-[#6E625B] text-base font-medium font-['Noto_Sans_TC'] leading-6">
                {item.activity_title ? (
                  <>
                    <span className="text-[#bc313d]">{item.voucher_name}</span><br />{item.activity_title}
                  </>
                ) : (
                  <span className="text-[#bc313d]">{item.voucher_name}</span>
                )}
              </span>
            </div>

            {/* Right section */}
            <div className="w-[80px] md:w-[104px] bg-[#C69C55] flex items-center justify-center shrink-0 border-l-2 border-dashed border-white ">
              <span className="text-white text-2xl md:text-[28px] font-bold font-['Noto_Sans_TC'] leading-10 tracking-tight">
                x{item.count}
              </span>
            </div>

            {/* Cutouts */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 md:w-7 md:h-7 bg-[#FCF6ED] rounded-full" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 md:w-7 md:h-7 bg-[#FCF6ED] rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
function ScoreBar({ available, total }) {
  const { t } = useLanguage();
  const pct = total > 0 ? Math.min(100, (available / total) * 100) : 0;
  return (
    <div className="w-full" style={{ padding: '16px 20px 8px 20px' }}>
      <div className="flex items-baseline justify-between mb-1">
        <span style={{ color: 'rgba(110,98,91,0.60)', fontSize: 13, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '19.5px' }}>
          {t('redeem.scoreBarLabel')}
        </span>
        <div className="flex items-baseline gap-1">
          <span style={{ color: '#C59B5A', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px', letterSpacing: 0.38 }}>
            {available.toLocaleString()}
          </span>
          <span style={{ color: 'rgba(110,98,91,0.30)', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px' }}>/</span>
          <span style={{ color: '#6E625B', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px', letterSpacing: 0.38 }}>
            {total.toLocaleString()}
          </span>
        </div>
      </div>
      <div className="rounded-full overflow-hidden" style={{ height: 8, background: 'rgba(110,98,91,0.12)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: '#BC313D' }}
        />
      </div>
    </div>
  );
}
const RedeemPage = () => {
  const { t, currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [ruleTarget, setRuleTarget] = useState(null);
  const [mascotItems, setMascotItems] = useState([]);
  const [mascotTotalPoints, setMascotTotalPoints] = useState(0);
  const [mascotLoading, setMascotLoading] = useState(true);

  const [voucherItems, setVoucherItems] = useState([]);
  const [voucherLoading, setVoucherLoading] = useState(true);

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [selectedMascot, setSelectedMascot] = useState(null);
  const [selectedMascotVouchers, setSelectedMascotVouchers] = useState([]);

  const [userAvailablePoint, setUserAvailablePoint] = useState(0);
  const [userTotalPoint, setUserTotalPoint] = useState(0);

  const navigate = useNavigate();
  const { setLayoutOptions } = usePageLayout();

  useEffect(() => {
    setLayoutOptions({
      noPaddingBottom: true,
      hideHeader: false,
      hideFooter: false,
    });
    return () => setLayoutOptions({});
  }, [setLayoutOptions]);

  const fetchData = () => {
    getMyProfile()
      .then((res) => {
        setUserAvailablePoint(res?.available_point || 0);
        setUserTotalPoint(res?.total_point || 0);
      })
      .catch(() => { })
      .finally(() => setLoading(false));

    setMascotLoading(true);
    getMascotList()
      .then((res) => {
        if (res?.items) {
          setMascotItems(res.items);
        }
        if (res?.settled_point) {
          setMascotTotalPoints(res.settled_point);
        }
      })
      .catch((err) => console.error('Failed to fetch mascots:', err))
      .finally(() => setMascotLoading(false));

    setVoucherLoading(true);
    getVoucherList()
      .then((res) => {
        const items = Array.isArray(res) ? res : (res?.items || res?.data || []);
        setVoucherItems(items);
      })
      .catch((err) => console.error('Failed to fetch vouchers:', err))
      .finally(() => setVoucherLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [currentLanguage]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => setRuleTarget(null), 300);
  };

  const handleRuleClick = () => {
    setRuleTarget('mascot');
    setIsOpen(true);
  };

  return (
    <PageLayout
      maxWidth="md"
      className="bg-[#FCF6ED]"
      hideHeader={true}
      hideFooter={true}
      hideBottomNav={true}
      noPaddingBottom={true}
    >
      <RulePages
        isOpen={isOpen}
        onClose={handleClose}
        target={ruleTarget}
      />
      <VoucherRedeemPopup
        isOpen={!!selectedVoucher}
        onClose={() => setSelectedVoucher(null)}
        onSuccess={() => {
          setSelectedVoucher(null);
          fetchData();
        }}
        voucher={selectedVoucher}
      />
      <MascotActionPopup
        isOpen={!!selectedMascot}
        onClose={() => setSelectedMascot(null)}
        mascot={selectedMascot}
        totalPoint={userAvailablePoint}
        voucherItems={selectedMascotVouchers}
        onSuccess={() => {
          setSelectedMascot(null);
          fetchData();
        }}
      />
      <div className="w-full relative flex flex-col justify-start items-center pb-20">
        {/* Responsive Header Banner */}
        <div className="w-full relative overflow-hidden">
          <img
            src="/image/else/redeem_header.png"
            alt="Redeem Header"
            className="w-full h-auto object-cover m-0 p-0 block"
          />
          <div className="absolute top-2 left-0 w-full h-full px-6 pt-3.5 pb-14 flex flex-col justify-center items-start pointer-events-none">
            <div className="text-[#BC313D] text-4xl font-black font-['Noto_Serif_TC'] leading-[56px] tracking-tight drop-shadow-md">積分兌換</div>
            <div className="text-[#6E625B] text-lg font-normal font-['Noto_Sans_TC'] leading-7 drop-shadow-md">Points redemption</div>
          </div>
        </div>
        <ScoreBar available={userAvailablePoint} total={userTotalPoint || 0} />
        {/* Content Area */}
        <div className="w-full px-4 sm:px-6 py-6">
          <div className="w-full relative flex flex-col gap-6">
            <div style={{ position: 'absolute', left: 6, top: 0, bottom: 0, width: 1, background: '#6E625B', pointerEvents: 'none' }} />
            <StaticSection title={t('redeem.sectionMascot')} onRuleClick={handleRuleClick} isFirstCollection={true}>
              <MascotGrid
                items={mascotItems}
                loading={mascotLoading}
                onMascotClick={(item) => {
                  if (item?.reward_id) {
                    const voucherItemsFiltered = voucherItems.filter(i => i.item_id === item?.item_id);
                    setSelectedMascot(item);
                    setSelectedMascotVouchers(voucherItemsFiltered);
                  }
                }}
              />
            </StaticSection>
            <StaticSection title={t('redeem.sectionVoucher')} onRuleClick={handleRuleClick}>
              <VoucherSection
                items={voucherItems}
                loading={voucherLoading}
                onVoucherClick={setSelectedVoucher}
              />
            </StaticSection>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default RedeemPage;
